import { getApiBaseUrl, getMerchantClient } from '@beatq/sdk-client';
import {
  ApiError,
  csrfHeaders,
  getAccessToken,
  isIdentitySessionError,
  mapErrorCode,
  readEntityFromEnvelope,
  readListFromEnvelope,
  readObjectFieldFromEnvelope,
} from '@beatq/shared';
import { emptyListPayload, isOptionalReadMiss } from '../../shell/merchantApiSoftFail.js';
import { floorAwareWrite } from './floorAwareWrite.js';
import { isVariableWeightItem } from './floorHelpers.js';

type ApiErr = { error?: { code: string; message: string } };

export function sdkErrorMessage(error: unknown): string {
  const e = error as ApiErr;
  if (e?.error && isIdentitySessionError(e.error.code)) {
    throw new ApiError(e.error.code, e.error.message);
  }
  return e?.error?.message ?? mapErrorCode(e?.error?.code ?? 'ERR-UNKNOWN');
}

export type KitchenTicketLine = {
  name?: string;
  productName?: string;
  quantity?: number | null;
  modifiers?: Array<{ name?: string } | string>;
  allergens?: string[];
  allergenRemoved?: string[];
  notes?: string | null;
};

export type KitchenOrderSummary = {
  id?: string;
  publicReference?: string | null;
  reference?: string | null;
  status?: string;
  promisedAt?: string | null;
  fulfilmentMode?: string | null;
  itemCount?: number | null;
  itemSummary?: string | null;
  linesSummary?: string | null;
  hasAllergenNote?: boolean | null;
  hasVariableWeightItems?: boolean | null;
  requiresAgeVerification?: boolean | null;
  customerFirstName?: string | null;
  customerName?: string | null;
  collectionCodeHint?: string | null;
  needsAttentionReason?: string | null;
  blocked?: boolean | null;
  weightBlocked?: boolean | null;
  recalled?: boolean | null;
  notes?: string | null;
  allergenNotes?: string | null;
  lines?: KitchenTicketLine[];
};

export type SuggestedSubstitute = {
  productName?: string | null;
  variantId?: string | null;
  substituteVariantId?: string | null;
  substituteProductId?: string | null;
  sku?: string | null;
  priceDeltaMinor?: number | null;
  priceDelta?: { minor?: number | null } | null;
  allergenSafe?: boolean | null;
  allergensIntroduced?: string[] | null;
};

export type PickListItem = {
  id?: string;
  itemId?: string;
  lineId?: string;
  name?: string;
  productName?: string;
  variantName?: string | null;
  imageUrl?: string | null;
  aisle?: string | null;
  shelf?: string | null;
  location?: string | null;
  quantity?: number | null;
  quantityRequired?: number | null;
  pickedQuantity?: number | null;
  quantityPicked?: number | null;
  status?: string;
  requiresWeight?: boolean | null;
  requiredWeightGrams?: number | null;
  minWeightGrams?: number | null;
  maxWeightGrams?: number | null;
  toleranceBp?: number | null;
  pickedWeightGrams?: number | null;
  weightGrams?: number | null;
  unitPricePerKgMinor?: number | null;
  estimatedLineTotalMinor?: number | null;
  barcode?: string | null;
  sku?: string | null;
  state?: string | null;
  batchRequired?: boolean | null;
  requiresBatch?: boolean | null;
  customerNote?: string | null;
  substitutionAllowed?: boolean | null;
  customerPreference?: string | null;
  suggestedSubstitutes?: SuggestedSubstitute[];
  isAgeRestricted?: boolean | null;
  requiresRefrigeration?: boolean | null;
};

export type PickListMeta = {
  sortedBy?: string | null;
  state?: string | null;
  orderReference?: string | null;
  progress?: {
    total?: number;
    picked?: number;
    unavailable?: number;
    substituted?: number;
  } | null;
  capabilities?: { canComplete?: boolean; completeBlockedReason?: string | null } | null;
  estimatedRemainingMinutes?: number | null;
};

export type FulfilmentTask = {
  orderId?: string;
  status?: string;
  station?: string | null;
  items?: PickListItem[];
  promisedAt?: string | null;
  notes?: string | null;
  allergenNotes?: string | null;
};

export type SubstitutionProposal = {
  id?: string;
  lineId?: string;
  substituteVariantId?: string;
  originalItemName?: string | null;
  proposedItemName?: string | null;
  status?: string;
  reason?: string | null;
  priceDifference?: { minor?: number | null; currency?: string | null } | null;
  priceDeltaMinor?: number | null;
};

export type FulfilmentCapacity = {
  maxConcurrentOrders?: number | null;
  currentLoad?: number | null;
  headroom?: number | null;
  accepting?: boolean | null;
  delayMinutes?: number | null;
};

export type KitchenDisplayDensity = 'floor' | 'dense';

export type KitchenDisplaySettings = {
  columnCount: 1 | 2 | 3;
  soundEnabled: boolean;
  density: KitchenDisplayDensity;
  recallWindowSeconds: number;
};

export type KitchenDisplaySettingsBundle = {
  displaySettings: KitchenDisplaySettings;
  capacity: FulfilmentCapacity;
};

export const KITCHEN_DISPLAY_SETTINGS_STORAGE_KEY = 'beatq.merchant.kitchenDisplaySettings';

export const DEFAULT_KITCHEN_DISPLAY_SETTINGS: KitchenDisplaySettings = {
  columnCount: 2,
  soundEnabled: true,
  density: 'floor',
  recallWindowSeconds: 60,
};

function flattenBoardOrders(envelope: unknown): KitchenOrderSummary[] {
  const direct = readListFromEnvelope<KitchenOrderSummary>(envelope, ['orders', 'board', 'items']);
  if (direct.length > 0) return direct;
  const root = ((envelope as { data?: Record<string, unknown> })?.data ?? envelope) as {
    columns?: Array<{ orders?: KitchenOrderSummary[] }>;
    board?: { columns?: Array<{ orders?: KitchenOrderSummary[] }> };
  };
  const columns = root?.columns ?? root?.board?.columns;
  if (!Array.isArray(columns)) return [];
  return columns.flatMap((col) => col.orders ?? []);
}

export function ordersFromEnvelope(envelope: unknown): KitchenOrderSummary[] {
  return flattenBoardOrders(envelope).map((order) => ({
    ...order,
    publicReference: order.publicReference ?? order.reference ?? null,
    customerFirstName:
      order.customerFirstName ??
      (typeof order.customerName === 'string' ? order.customerName.split(' ')[0] : null),
  }));
}

function normalizePickItem(item: PickListItem): PickListItem {
  const quantity = item.quantity ?? item.quantityRequired ?? null;
  const pickedQuantity = item.pickedQuantity ?? item.quantityPicked ?? null;
  const requiredWeightGrams = item.requiredWeightGrams ?? null;
  const pickedWeightGrams = item.pickedWeightGrams ?? item.weightGrams ?? null;
  return {
    ...item,
    id: item.id ?? item.itemId ?? item.lineId,
    itemId: item.itemId ?? item.id,
    lineId: item.lineId ?? item.id,
    name: item.name ?? item.productName,
    quantity,
    quantityRequired: item.quantityRequired ?? quantity,
    pickedQuantity,
    quantityPicked: item.quantityPicked ?? pickedQuantity,
    requiredWeightGrams,
    pickedWeightGrams,
    weightGrams: item.weightGrams ?? pickedWeightGrams,
    status: item.status ?? item.state ?? undefined,
    requiresWeight:
      item.requiresWeight === true ||
      isVariableWeightItem({
        requiresWeight: item.requiresWeight,
        requiredWeightGrams,
      }),
    requiresBatch: item.requiresBatch === true || item.batchRequired === true,
    suggestedSubstitutes: item.suggestedSubstitutes ?? [],
  };
}

export function pickListFromEnvelope(envelope: unknown): PickListItem[] {
  return readListFromEnvelope<PickListItem>(envelope, ['items', 'pickList']).map(normalizePickItem);
}

export function pickListMetaFromEnvelope(envelope: unknown): PickListMeta {
  const root = (envelope as { data?: Record<string, unknown> })?.data ?? envelope;
  const data = (root ?? {}) as PickListMeta & {
    progress?: PickListMeta['progress'];
    state?: string;
    orderReference?: string;
  };
  return {
    sortedBy: data.sortedBy ?? null,
    state: data.state ?? null,
    orderReference: data.orderReference ?? null,
    progress: data.progress ?? null,
    capabilities: data.capabilities ?? null,
    estimatedRemainingMinutes:
      typeof (data as { estimatedRemainingMinutes?: unknown }).estimatedRemainingMinutes ===
      'number'
        ? (data as { estimatedRemainingMinutes: number }).estimatedRemainingMinutes
        : null,
  };
}

export function groupPickItemsByAisle(items: PickListItem[]): Map<string, PickListItem[]> {
  const groups = new Map<string, PickListItem[]>();
  const unassigned: PickListItem[] = [];
  for (const item of items) {
    const aisle = item.aisle?.trim();
    if (!aisle) {
      unassigned.push(item);
      continue;
    }
    const list = groups.get(aisle) ?? [];
    list.push(item);
    groups.set(aisle, list);
  }
  if (unassigned.length > 0) {
    groups.set('Unassigned', unassigned);
  }
  return groups;
}

export function fulfilmentTaskFromEnvelope(envelope: unknown): FulfilmentTask | undefined {
  return readEntityFromEnvelope<FulfilmentTask>(envelope, ['task']);
}

export function substitutionsFromEnvelope(envelope: unknown): SubstitutionProposal[] {
  return readListFromEnvelope<SubstitutionProposal>(envelope, ['substitutions']).map((row) => ({
    ...row,
    originalItemName: row.originalItemName ?? row.lineId ?? 'Item',
    proposedItemName: row.proposedItemName ?? row.substituteVariantId ?? 'Substitute',
  }));
}

/** Pick lines that still need a positive weight before pack-out. */
export function weightBlockersFromPickList(items: PickListItem[]): PickListItem[] {
  return items.filter(
    (item) =>
      isVariableWeightItem(item) &&
      (item.pickedWeightGrams == null || Number(item.pickedWeightGrams) <= 0) &&
      (item.weightGrams == null || Number(item.weightGrams) <= 0),
  );
}

export function positiveWeightGrams(raw: string): number {
  const grams = Number(raw);
  if (!Number.isInteger(grams) || grams <= 0) {
    throw new Error('Weight must be a positive whole number of grams');
  }
  return grams;
}

export class MerchantHttpStatusError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'MerchantHttpStatusError';
    this.status = status;
  }
}

export function capacityFromEnvelope(envelope: unknown): FulfilmentCapacity | undefined {
  const raw = readObjectFieldFromEnvelope<{
    tasksInProgress?: number;
    headroom?: number;
    accepting?: boolean;
    maxConcurrentOrders?: number;
    currentLoad?: number;
    delayMinutes?: number;
  }>(envelope, ['capacity']);
  if (!raw) return undefined;

  const currentLoad = raw.currentLoad ?? raw.tasksInProgress ?? null;
  const headroom = raw.headroom ?? null;
  const maxConcurrentOrders =
    raw.maxConcurrentOrders ??
    (currentLoad != null && headroom != null ? currentLoad + headroom : null);

  return {
    maxConcurrentOrders,
    currentLoad,
    headroom,
    accepting: raw.accepting ?? null,
    delayMinutes: raw.delayMinutes ?? null,
  };
}

export function kitchenDisplaySettingsFromEnvelope(
  envelope: unknown,
): KitchenDisplaySettings | undefined {
  const raw = readObjectFieldFromEnvelope<Partial<KitchenDisplaySettings>>(envelope, [
    'displaySettings',
  ]);
  if (!raw) return undefined;

  const columnCount =
    raw.columnCount === 1 || raw.columnCount === 2 || raw.columnCount === 3
      ? raw.columnCount
      : DEFAULT_KITCHEN_DISPLAY_SETTINGS.columnCount;

  return {
    columnCount,
    soundEnabled: raw.soundEnabled ?? DEFAULT_KITCHEN_DISPLAY_SETTINGS.soundEnabled,
    density: raw.density === 'dense' ? 'dense' : 'floor',
    recallWindowSeconds:
      typeof raw.recallWindowSeconds === 'number'
        ? raw.recallWindowSeconds
        : DEFAULT_KITCHEN_DISPLAY_SETTINGS.recallWindowSeconds,
  };
}

export function kitchenDisplaySettingsBundleFromEnvelope(
  envelope: unknown,
): KitchenDisplaySettingsBundle | undefined {
  const displaySettings = kitchenDisplaySettingsFromEnvelope(envelope);
  const capacity = capacityFromEnvelope(envelope);
  if (!displaySettings || !capacity) return undefined;
  return { displaySettings, capacity };
}

export function cacheKitchenDisplaySettings(settings: KitchenDisplaySettings): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(KITCHEN_DISPLAY_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
}

export function readCachedKitchenDisplaySettings(): KitchenDisplaySettings {
  if (typeof localStorage === 'undefined') return DEFAULT_KITCHEN_DISPLAY_SETTINGS;
  try {
    const raw = localStorage.getItem(KITCHEN_DISPLAY_SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_KITCHEN_DISPLAY_SETTINGS;
    return (
      kitchenDisplaySettingsFromEnvelope({ displaySettings: JSON.parse(raw) }) ??
      DEFAULT_KITCHEN_DISPLAY_SETTINGS
    );
  } catch {
    return DEFAULT_KITCHEN_DISPLAY_SETTINGS;
  }
}

async function merchantFulfilmentRequest<T>(
  method: 'GET' | 'PATCH',
  path: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
      ...csrfHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const raw = await response.text();
  if (!raw) {
    if (response.status === 404) {
      throw new MerchantHttpStatusError(
        404,
        'Kitchen settings are unavailable right now. Refresh the page or try again in a moment.',
      );
    }
    throw new MerchantHttpStatusError(
      response.status,
      `Kitchen settings request failed (${response.status}).`,
    );
  }

  let payload: { data?: T; error?: ApiErr['error'] };
  try {
    payload = JSON.parse(raw) as { data?: T; error?: ApiErr['error'] };
  } catch {
    throw new MerchantHttpStatusError(
      response.status,
      response.status === 404
        ? 'Kitchen settings are unavailable right now. Refresh the page or try again in a moment.'
        : 'Kitchen settings returned an invalid response. Refresh the page and try again.',
    );
  }

  if (!response.ok || payload.error) {
    throw new MerchantHttpStatusError(response.status, sdkErrorMessage(payload));
  }
  if (payload.data == null) {
    throw new MerchantHttpStatusError(response.status, 'Kitchen settings response was empty.');
  }
  return payload.data;
}

export function findPickItem(items: PickListItem[], itemId: string): PickListItem | undefined {
  return items.find((item) => item.id === itemId || item.itemId === itemId);
}

export function isCollectionReady(order: KitchenOrderSummary | undefined): boolean {
  const status = order?.status ?? '';
  return ['READY_FOR_COLLECTION', 'COLLECTION', 'READY', 'AWAITING_COLLECTION'].some((value) =>
    status.includes(value),
  );
}

export function isKitchenPrepQueueOrder(order: KitchenOrderSummary | undefined): boolean {
  const status = order?.status ?? '';
  return status === 'ACCEPTED' || status === 'PREPARING';
}

export function canKitchenStartPrep(order: KitchenOrderSummary | undefined): boolean {
  return order?.status === 'ACCEPTED';
}

export function canKitchenFlagDelay(order: KitchenOrderSummary | undefined): boolean {
  const status = order?.status ?? '';
  return status === 'ACCEPTED' || status === 'PREPARING';
}

export async function getKitchenBoard(params?: Record<string, string>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/board', {
    params: { query: params as never },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function startPreparation(orderId: string, body: Record<string, unknown> = {}) {
  return floorAwareWrite(
    { kind: 'start_prep', label: 'Start preparation', orderId, payload: body },
    () => startPreparationRequest(orderId, body),
  );
}

async function startPreparationRequest(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/preparing', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function flagOrderDelay(orderId: string, body: Record<string, unknown>) {
  return floorAwareWrite({ kind: 'flag_delay', label: 'Flag delay', orderId, payload: body }, () =>
    flagOrderDelayRequest(orderId, body),
  );
}

async function flagOrderDelayRequest(orderId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/delay', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function bumpPickItem(
  orderId: string,
  itemId: string,
  body: Record<string, unknown> = {},
) {
  return floorAwareWrite(
    { kind: 'bump_item', label: 'Bump item', orderId, itemId, payload: body },
    () => bumpPickItemRequest(orderId, itemId, body),
  );
}

async function bumpPickItemRequest(
  orderId: string,
  itemId: string,
  body: Record<string, unknown> = {},
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/pick-list/items/{itemId}/bump',
    {
      params: { path: { id: orderId, itemId } },
      body: body as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function bumpTicketItems(orderId: string, itemIds: string[]) {
  for (const itemId of itemIds) {
    await bumpPickItem(orderId, itemId);
  }
}

export async function getFulfilmentTask(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/fulfilment/tasks/{orderId}', {
    params: { path: { orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function recallTicket(orderId: string, body: Record<string, unknown> = {}) {
  return floorAwareWrite(
    { kind: 'recall_ticket', label: 'Recall ticket', orderId, payload: body },
    () => recallTicketRequest(orderId, body),
  );
}

async function recallTicketRequest(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/ticket/recall', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getFulfilmentCapacity(params?: Record<string, string>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/fulfilment/capacity', {
    params: { query: params as never },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function getKitchenDisplaySettings() {
  return merchantFulfilmentRequest<{
    displaySettings: KitchenDisplaySettings;
    capacity: Record<string, unknown>;
  }>('GET', '/merchant/v1/fulfilment/display-settings');
}

export async function updateKitchenDisplaySettings(body: Partial<KitchenDisplaySettings>) {
  return merchantFulfilmentRequest<{
    displaySettings: KitchenDisplaySettings;
    capacity: Record<string, unknown>;
  }>('PATCH', '/merchant/v1/fulfilment/display-settings', body);
}

export async function getPickList(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}/pick-list', {
    params: { path: { id: orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function pickItem(orderId: string, itemId: string, body: Record<string, unknown>) {
  return floorAwareWrite(
    { kind: 'pick_item', label: 'Pick item', orderId, itemId, payload: body },
    () => pickItemRequest(orderId, itemId, body),
  );
}

async function pickItemRequest(orderId: string, itemId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/pick-list/items/{itemId}/pick',
    {
      params: { path: { id: orderId, itemId } },
      body: body as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function markItemUnavailable(
  orderId: string,
  itemId: string,
  body: Record<string, unknown>,
) {
  return floorAwareWrite(
    { kind: 'mark_unavailable', label: 'Mark unavailable', orderId, itemId, payload: body },
    () => markItemUnavailableRequest(orderId, itemId, body),
  );
}

async function markItemUnavailableRequest(
  orderId: string,
  itemId: string,
  body: Record<string, unknown>,
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/pick-list/items/{itemId}/unavailable',
    {
      params: { path: { id: orderId, itemId } },
      body: body as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function recordPickWeight(
  orderId: string,
  itemId: string,
  body: Record<string, unknown>,
) {
  return floorAwareWrite(
    { kind: 'record_weight', label: 'Record weight', orderId, itemId, payload: body },
    () => recordPickWeightRequest(orderId, itemId, body),
  );
}

async function recordPickWeightRequest(
  orderId: string,
  itemId: string,
  body: Record<string, unknown>,
) {
  const weightGrams =
    typeof body.weightGrams === 'number'
      ? body.weightGrams
      : positiveWeightGrams(String(body.weightGrams ?? ''));
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/pick-list/items/{itemId}/weight',
    {
      params: { path: { id: orderId, itemId } },
      body: { weightGrams } as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function proposeSubstitution(orderId: string, body: Record<string, unknown>) {
  return floorAwareWrite(
    { kind: 'propose_substitution', label: 'Propose substitution', orderId, payload: body },
    () => proposeSubstitutionRequest(orderId, body),
  );
}

async function proposeSubstitutionRequest(orderId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/substitutions', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function listSubstitutions(orderId: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}/substitutions', {
    params: { path: { id: orderId } },
  });
  if (isOptionalReadMiss(response.status)) return emptyListPayload('substitutions');
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function completePickList(orderId: string, body: Record<string, unknown> = {}) {
  return floorAwareWrite(
    { kind: 'complete_pick', label: 'Complete pick list', orderId, payload: body },
    () => completePickListRequest(orderId, body),
  );
}

async function completePickListRequest(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/pick-list/complete',
    {
      params: { path: { id: orderId } },
      body: body as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function verifyCollection(orderId: string, handoverCode: string) {
  return floorAwareWrite(
    {
      kind: 'verify_collection',
      label: 'Verify collection',
      orderId,
      payload: { handoverCode },
    },
    () => verifyCollectionRequest(orderId, handoverCode),
  );
}

async function verifyCollectionRequest(orderId: string, handoverCode: string) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/verify-collection',
    {
      params: { path: { id: orderId } },
      body: { handoverCode: handoverCode.toUpperCase() },
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function completeHandover(
  orderId: string,
  body: { handoverCode?: string; note?: string; handoverType?: string } = {},
) {
  return floorAwareWrite(
    { kind: 'complete_handover', label: 'Complete handover', orderId, payload: body },
    () => completeHandoverRequest(orderId, body),
  );
}

async function completeHandoverRequest(
  orderId: string,
  body: { handoverCode?: string; note?: string; handoverType?: string } = {},
) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/handover', {
    params: { path: { id: orderId } },
    body: {
      handoverCode: body.handoverCode?.trim().toUpperCase() || undefined,
      note: body.note?.trim() || undefined,
      handoverType: body.handoverType ?? 'TO_CUSTOMER',
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function refuseHandover(orderId: string, body: Record<string, unknown>) {
  return floorAwareWrite(
    { kind: 'refuse_handover', label: 'Refuse handover', orderId, payload: body },
    () => refuseHandoverRequest(orderId, body),
  );
}

async function refuseHandoverRequest(orderId: string, body: Record<string, unknown>) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/handover/refuse', {
    params: { path: { id: orderId } },
    body: {
      ...(typeof body.reason === 'string' ? { reason: body.reason } : {}),
    } as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export type AgeCheckPayload = {
  qualifyingDateOfBirth?: string | null;
  qualifyingDateOfBirthLabel?: string | null;
  statutoryMinimumAge?: number | null;
  challengeAge?: number | null;
  restrictedLines?: Array<{ lineId?: string; productName?: string; minimumAge?: number }>;
  acceptedIdentification?: string[];
  acceptedIdentificationTypes?: string[];
  outcome?: string | null;
  capture?: { documentImage?: boolean; documentNumber?: boolean; dateOfBirth?: boolean };
};

export function ageCheckFromEnvelope(envelope: unknown): AgeCheckPayload | undefined {
  const root = ((envelope as { data?: AgeCheckPayload })?.data ?? envelope) as AgeCheckPayload;
  if (!root || typeof root !== 'object') return undefined;
  if (!root.qualifyingDateOfBirth && !root.qualifyingDateOfBirthLabel && !root.restrictedLines) {
    return undefined;
  }
  return root;
}

export async function getAgeCheck(orderId: string): Promise<AgeCheckPayload> {
  const client = getMerchantClient();
  const { data, error, response } = await client.GET('/merchant/v1/orders/{id}/age-check', {
    params: { path: { id: orderId } },
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return ageCheckFromEnvelope(data) ?? (data as { data?: AgeCheckPayload } | undefined)?.data ?? {};
}

export async function recordAgeCheck(orderId: string, body: Record<string, unknown>) {
  return floorAwareWrite(
    { kind: 'record_age_check', label: 'Record age check', orderId, payload: body },
    () => recordAgeCheckRequest(orderId, body),
  );
}

async function recordAgeCheckRequest(orderId: string, body: Record<string, unknown>) {
  const payload = {
    ...(typeof body.outcome === 'string' ? { outcome: body.outcome } : {}),
    ...(typeof body.verified === 'boolean' ? { verified: body.verified } : {}),
    ...(typeof body.documentType === 'string' && body.documentType.trim()
      ? { documentType: body.documentType.trim() }
      : {}),
    ...(typeof body.ageVerificationMethod === 'string'
      ? { ageVerificationMethod: body.ageVerificationMethod }
      : {}),
  };
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/age-check', {
    params: { path: { id: orderId } },
    body: payload as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function markOrderReady(orderId: string, body: Record<string, unknown> = {}) {
  return floorAwareWrite({ kind: 'mark_ready', label: 'Mark ready', orderId, payload: body }, () =>
    markOrderReadyRequest(orderId, body),
  );
}

async function markOrderReadyRequest(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST('/merchant/v1/orders/{id}/ready', {
    params: { path: { id: orderId } },
    body: body as never,
  });
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}

export async function overrideFulfilmentGate(orderId: string, body: Record<string, unknown> = {}) {
  return floorAwareWrite(
    { kind: 'override_gate', label: 'Accept weight variance', orderId, payload: body },
    () => overrideFulfilmentGateRequest(orderId, body),
  );
}

async function overrideFulfilmentGateRequest(orderId: string, body: Record<string, unknown> = {}) {
  const client = getMerchantClient();
  const { data, error, response } = await client.POST(
    '/merchant/v1/orders/{id}/fulfilment-gate/override',
    {
      params: { path: { id: orderId } },
      body: { reason: 'WEIGHT_VARIANCE', ...body } as never,
    },
  );
  if (!response.ok || error) throw new Error(sdkErrorMessage(error));
  return data;
}
